import { Button, Paper, TextInput, Switch, Badge, Text, Title, Group, Stack, ActionIcon } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import { MessageCircle, Plus, Send, TestTube, X } from "lucide-react";
import React, { useState } from "react";
import {
  sendWhatsAppProduction,
  sendWhatsAppSheet,
  sendWhatsAppTest,
} from "../utils/api";


const WhatsAppNotification = () => {
  const [testLoading, setTestLoading] = useState(false);
  const [productionLoading, setProductionLoading] = useState(false);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [isTestMode, setIsTestMode] = useState(true);
  const [isSheetMode, setIsSheetMode] = useState(false);
  const [formId, setFormId] = useState("");
  const [formLink, setFormLink] = useState("");

  const resetForm = () => {
    setFormId("");
    setFormLink("");
    setPhoneNumbers([]);
    setNewPhoneNumber("");
  };

  const addPhoneNumber = () => {
    if (newPhoneNumber.trim() && !phoneNumbers.includes(newPhoneNumber.trim())) {
      setPhoneNumbers([...phoneNumbers, newPhoneNumber.trim()]);
      setNewPhoneNumber("");
    }
  };

  const removePhoneNumber = (phoneNumber) => {
    setPhoneNumbers(phoneNumbers.filter((num) => num !== phoneNumber));
  };

  const handleModeSwitch = (e) => {
    setIsTestMode(!e.currentTarget.checked);
    setIsSheetMode(false);
    resetForm();
  };

  const handleTestSend = async () => {
    if (!formId) {
      notifications.show({ message: "Please enter the ID", color: "red" });
      return;
    }
    if (phoneNumbers.length === 0) {
      notifications.show({ message: "Please add at least one phone number", color: "red" });
      return;
    }

    setTestLoading(true);
    try {
      const [success, data] = await sendWhatsAppTest({ id: formId, phoneNumbers });
      if (success) {
        notifications.show({ message: "Test WhatsApp notification sent successfully!", color: "green" });
        resetForm();
      } else {
        notifications.show({ message: data?.message || "Failed to send test notification", color: "red" });
      }
    } catch {
      notifications.show({ message: "An error occurred while sending test notification", color: "red" });
    } finally {
      setTestLoading(false);
    }
  };

  const handleProductionSend = async () => {
    if (!formId) {
      notifications.show({ message: "Please enter the ID", color: "red" });
      return;
    }
    setProductionLoading(true);
    try {
      const [success, data] = await sendWhatsAppProduction({ id: formId });
      if (success) {
        notifications.show({ message: "Production WhatsApp notification sent successfully!", color: "green" });
        resetForm();
      } else {
        notifications.show({ message: data?.message || "Failed to send production notification", color: "red" });
      }
    } catch {
      notifications.show({ message: "An error occurred while sending production notification", color: "red" });
    } finally {
      setProductionLoading(false);
    }
  };

  const handleSheetSend = async () => {
    if (!formId || !formLink) {
      notifications.show({ message: "Please fill all the fields", color: "red" });
      return;
    }
    setSheetLoading(true);
    try {
      const [success, data] = await sendWhatsAppSheet({ id: formId, link: formLink });
      if (success) {
        notifications.show({ message: "Sheet WhatsApp notification sent successfully!", color: "green" });
        resetForm();
      } else {
        notifications.show({ message: data?.message || "Failed to send sheet notification", color: "red" });
      }
    } catch {
      notifications.show({ message: "An error occurred while sending sheet notification", color: "red" });
    } finally {
      setSheetLoading(false);
    }
  };

  const handleSend = isTestMode ? handleTestSend : isSheetMode ? handleSheetSend : handleProductionSend;
  const sendLoading = isTestMode ? testLoading : isSheetMode ? sheetLoading : productionLoading;

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title order={2} className="text-white flex items-center gap-2">
          <MessageCircle className="text-green-500" size={28} />
          WhatsApp Notifications
        </Title>

        <div className="mt-4 flex items-center gap-3">
          <span className="text-gray-300">Mode:</span>
          <Group gap="xs">
            <span className={`text-sm ${isTestMode ? "text-yellow-400" : "text-gray-400"}`}>Test</span>
            <Switch checked={!isTestMode} onChange={handleModeSwitch} />
            <span className={`text-sm ${!isTestMode ? "text-green-400" : "text-gray-400"}`}>Production</span>
          </Group>
        </div>

        {!isTestMode && (
          <div className="mt-3 flex items-center gap-3">
            <span className="text-gray-300">Type:</span>
            <Group gap="xs">
              <span className={`text-sm ${!isSheetMode ? "text-green-400" : "text-gray-400"}`}>Regular</span>
              <Switch
                checked={isSheetMode}
                onChange={(e) => {
                  setIsSheetMode(e.currentTarget.checked);
                  resetForm();
                }}
              />
              <span className={`text-sm ${isSheetMode ? "text-blue-400" : "text-gray-400"}`}>Sheet</span>
            </Group>
          </div>
        )}
      </div>

      <Paper withBorder p="md" className="bg-gray-900 border-gray-700 max-w-2xl mx-auto">
        <Stack>
          <TextInput
            label="ID"
            placeholder="Enter ID"
            required
            value={formId}
            onChange={(e) => setFormId(e.target.value)}
          />

          {!isTestMode && isSheetMode && (
            <TextInput
              label="Link"
              placeholder="Enter link"
              required
              value={formLink}
              onChange={(e) => setFormLink(e.target.value)}
            />
          )}

          {isTestMode && (
            <div>
              <Text size="sm" fw={500} mb={4}>Phone Numbers</Text>
              <Group mb="xs">
                <TextInput
                  placeholder="Enter phone number (e.g., +1234567890)"
                  value={newPhoneNumber}
                  onChange={(e) => setNewPhoneNumber(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addPhoneNumber()}
                  style={{ flex: 1 }}
                />
                <Button leftSection={<Plus size={16} />} onClick={addPhoneNumber}>Add</Button>
              </Group>
              {phoneNumbers.length > 0 && (
                <Group gap="xs" wrap="wrap">
                  {phoneNumbers.map((phoneNumber, index) => (
                    <Badge
                      key={index}
                      rightSection={
                        <ActionIcon size="xs" variant="transparent" onClick={() => removePhoneNumber(phoneNumber)}>
                          <X size={10} />
                        </ActionIcon>
                      }
                    >
                      {phoneNumber}
                    </Badge>
                  ))}
                </Group>
              )}
            </div>
          )}

          <Button
            leftSection={<Send size={16} />}
            loading={sendLoading}
            onClick={handleSend}
            color={isTestMode ? "yellow" : isSheetMode ? "blue" : "green"}
            fullWidth
          >
            {isTestMode ? "Send Test Notification" : isSheetMode ? "Send Sheet Notification" : "Send Production Notification"}
          </Button>
        </Stack>
      </Paper>
    </div>
  );
};

export default WhatsAppNotification;
